import { memo, type ReactNode, useMemo } from 'react';
import cn from 'classnames';
import { HStack, Text, VStack } from '@direct-frontend/components';
import { formatBonusWord } from '../../lib/bonus';
import { makeModifierClassname } from '../../lib/makeModifierClassname';
import { TiltedBadge } from '../TiltedBadge';
import styles from './BonusBadge.module.css';

export interface BonusBadgeProps {
    children?: ReactNode;

    description: ReactNode;

    /**
     * Показывать ли подпись с формой слова "бонус"
     */
    showBonusLabel?: boolean;

    /**
     * Иконка для отображения в правой части бейджа
     */
    icon?: 'present' | 'ruble-bag';
}

const modifier = makeModifierClassname(styles, 'container');

export const BonusBadge = memo(function BonusBadge({
    children,
    description,
    showBonusLabel = true,
    icon = 'ruble-bag',
}: BonusBadgeProps) {
    const mainBadge = useMemo(
        () => (
            <TiltedBadge className={styles.mainBadge}>
                <Text size="l" bold>
                    {children}
                </Text>
            </TiltedBadge>
        ),
        [children]
    );

    const bonusLabel = useMemo(() => {
        if (!showBonusLabel) {
            return null;
        }

        const bonusCount = typeof children === 'number' ? children : 0;

        return formatBonusWord(bonusCount);
    }, [children, showBonusLabel]);

    const badgeContent = showBonusLabel ? (
        <VStack className={styles.badgeWrapper} alignItems="flex-start">
            {mainBadge}
            <TiltedBadge variant="primary" className={styles.secondBadge}>
                <Text size="xs">{bonusLabel}</Text>
            </TiltedBadge>
        </VStack>
    ) : (
        mainBadge
    );

    const descriptionContent =
        typeof description === 'string' ? (
            <Text size="mc" className={styles.description}>
                {description}
            </Text>
        ) : (
            <div className={styles.description}>{description}</div>
        );

    return (
        <HStack className={cn(modifier.className, modifier('icon', icon))} gap="16" alignItems="center">
            {badgeContent}
            {descriptionContent}
        </HStack>
    );
});
